import React, { useMemo, useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import logo from "../../assests/images/Bgpccare.png";
import { getProductEnquiryLink } from "../../utils/whatsapp";
import { cachedFetchJson } from "../../utils/apiCache";


const ITEMS_PER_PAGE = 12;

const containerVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.08 } },
};

const cardVariants = {
  hidden: { opacity: 0, y: 40, scale: 0.96 },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: { duration: 0.5, ease: "easeOut" },
  },
};

const Products = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cartCount, setCartCount] = useState(0);
  const [showToast, setShowToast] = useState(false);
  const [toastMsg, setToastMsg] = useState("");
  const [reviewCounts, setReviewCounts] = useState({});

  const getAssetBaseUrl = () => {
    const rawBase =
      process.env.REACT_APP_ASSET_BASE_URL ||
      process.env.REACT_APP_BACKEND ||
      process.env.REACT_APP_API_BASE_URL ||
      process.env.REACT_APP_BACKEND_URL ||
      "http://localhost:8000";
    return String(rawBase).replace(/\/$/, "").replace(/\/api\/v1$/, "");
  };

const getCart = () => {
  const cart = localStorage.getItem("cart");
  return cart ? JSON.parse(cart) : [];
};

const handleAddToCart = (product) => {
  const cart = getCart();
  const existing = cart.find((item) => item.id === product.id);

  let updatedCart;

  if (existing) {
    updatedCart = cart;
    setToastMsg("Product already in cart");
  } else {
    updatedCart = [...cart, { ...product, item_type: "product", quantity: 1 }];
    setToastMsg("Product added to cart!");
  }

  localStorage.setItem("cart", JSON.stringify(updatedCart));
  setCartCount(updatedCart.length);

  window.dispatchEvent(new Event("cartUpdated"));

  setShowToast(true);
  setTimeout(() => setShowToast(false), 2000);
};

  const navigate = useNavigate();
  const location = useLocation();
  const searchQuery = (new URLSearchParams(location.search).get("q") || "").trim().toLowerCase();

  const normalizeToken = (value) =>
    String(value || "")
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "")
      .trim();

  const searchTokens = searchQuery
    .split(/\s+/)
    .map(normalizeToken)
    .filter(Boolean);

  const bestKeywords = ["best", "top", "popular", "recommended", "rating", "high"];
  const newKeywords = ["new", "latest", "recent", "naya", "fresh"];
  const stopWords = ["laptop", "laptops", "product", "products", "pc", "care"];
  const keywordAliases = {
    itel: ["intel"],
    intal: ["intel"],
    intel: ["intel"],
    ryzel: ["ryzen"],
    rizen: ["ryzen"],
    ryzen: ["ryzen"],
    amd: ["amd"],
    ram: ["ram", "gb"],
    ssd: ["ssd"],
    hdd: ["hdd"],
  };

  const isBestIntent = searchTokens.some((t) => bestKeywords.includes(t));
  const isNewIntent = searchTokens.some((t) => newKeywords.includes(t));

  const queryTokens = searchTokens.filter(
    (t) => !bestKeywords.includes(t) && !newKeywords.includes(t) && !stopWords.includes(t)
  );

  const [filters, setFilters] = useState({
    brand: [],
    ram: [],
    storage: [],
    processor: [],
  });

  const [sortBy, setSortBy] = useState("latest");
  const [currentPage, setCurrentPage] = useState(1);
  const [showSort, setShowSort] = useState(false);
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);

  const [hoverIndex, setHoverIndex] = useState({});
  const hoverTimers = useRef({});

  useEffect(() => {
    setCurrentPage(1);
  }, [filters, sortBy, searchQuery]);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const base =
          process.env.REACT_APP_API_BASE_URL ||
          process.env.REACT_APP_BACKEND_URL ||
          process.env.REACT_APP_BACKEND ||
          "http://localhost:8000/api/v1";
        const apiBase = String(base).replace(/\/$/, "");
        const data = await cachedFetchJson(`${apiBase}/products?status=active`, {
          ttlMs: 120000,
        });

        const assetBaseUrl = getAssetBaseUrl();
        const formatted = data.map((p) => {
          let imagesArray = [];

          try {
            imagesArray = p.images ? JSON.parse(p.images) : [];
          } catch {
            imagesArray = [];
          }

          return {
            id: p.id,
            name: p.name,
            brand: p.brand,
            ram: p.ram,
            storage: p.storage,
            processor: p.processor,
            gpu: p.gpu,
            os: p.os,
            category: p.category,
            description: p.description,
            screen_size: p.screen_size,
            weight: p.weight,
            color: p.color,
            condition: p.condition,
            warranty: p.warranty,
            rating: Number(p.rating || 0),
            reviews: 0,
            newPrice: Number(p.selling_price || 0),
            oldPrice: Number(p.original_price || 0),
            off:
              p.original_price && p.selling_price
                ? Math.round(
                    ((p.original_price - p.selling_price) / p.original_price) *
                      100
                  )
                : 0,
            images: imagesArray.map((img) => `${assetBaseUrl}/uploads/${img}`),
          };
        });

        const sorted = [...formatted].sort((a, b) => b.id - a.id);
        const latestIds = new Set(sorted.slice(0, 4).map((p) => p.id));
        const withoutLatest = sorted.filter((p) => !latestIds.has(p.id));

        setProducts(withoutLatest);
      } catch (error) {
        console.error("Error fetching products:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, []);


  const toggleFilter = (type, value) => {
    setFilters((prev) => ({
      ...prev,
      [type]: prev[type].includes(value)
        ? prev[type].filter((v) => v !== value)
        : [...prev[type], value],
    }));
  };

  /* ================= FILTER + SORT ================= */
  const filteredProducts = useMemo(() => {
    let data = products.filter((p) => {
      const searchableText = [
        p.name,
        p.brand,
        p.processor,
        p.ram,
        p.storage,
        p.gpu,
        p.os,
        p.category,
        p.description,
        p.screen_size,
        p.weight,
        p.color,
        p.condition,
        p.warranty,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      const queryOk =
        queryTokens.length === 0 ||
        queryTokens.every((token) => {
          const aliases = [token, ...(keywordAliases[token] || [])];
          return aliases.some((term) => searchableText.includes(term));
        });

      const brandOk =
        filters.brand.length === 0 ||
        filters.brand.includes(p.brand);

      const ramOk =
        filters.ram.length === 0 ||
        filters.ram.includes(p.ram);

      const storageOk =
        filters.storage.length === 0 ||
        filters.storage.includes(p.storage);

      const processorOk =
        filters.processor.length === 0 ||
        filters.processor.includes(p.processor);

      return queryOk && brandOk && ramOk && storageOk && processorOk;
    });

    if (isBestIntent) {
      data.sort((a, b) => {
        if (b.rating !== a.rating) return b.rating - a.rating;
        return b.id - a.id;
      });
    } else if (isNewIntent) {
      data.sort((a, b) => b.id - a.id);
    } else if (sortBy === "priceLow") {
      data.sort((a, b) => a.newPrice - b.newPrice);
    } else if (sortBy === "priceHigh") {
      data.sort((a, b) => b.newPrice - a.newPrice);
    } else if (sortBy === "rating") {
      data.sort((a, b) => b.rating - a.rating);
    } else {
      data.sort((a, b) => b.id - a.id);
    }

    return data;
  }, [filters, sortBy, products, queryTokens, isBestIntent, isNewIntent]);

  /* ================= PAGINATION ================= */
  const totalPages = Math.ceil(filteredProducts.length / ITEMS_PER_PAGE);

  const paginatedProducts = useMemo(() => {
    const start = (currentPage - 1) * ITEMS_PER_PAGE;
    return filteredProducts.slice(start, start + ITEMS_PER_PAGE);
  }, [filteredProducts, currentPage]);

  useEffect(() => {
    const apiBase = (
      process.env.REACT_APP_API_BASE_URL ||
      process.env.REACT_APP_BACKEND_URL ||
      process.env.REACT_APP_BACKEND ||
      "http://localhost:8000/api/v1"
    ).replace(/\/$/, "");

    let cancelled = false;
    const visibleIds = paginatedProducts.map((p) => p.id).filter(Boolean);
    if (visibleIds.length === 0) return undefined;

    const loadCounts = async () => {
      const counts = await Promise.all(
        visibleIds.map(async (pid) => {
          try {
            let list = await cachedFetchJson(
              `${apiBase}/reviews?product_id=${encodeURIComponent(pid)}&status=active`,
              { ttlMs: 120000 },
            );
            if (!Array.isArray(list)) {
              list = await cachedFetchJson(
                `${apiBase}/review?product_id=${encodeURIComponent(pid)}&status=active`,
                { ttlMs: 120000 },
              );
            }
            return [pid, Array.isArray(list) ? list.length : 0];
          } catch {
            return [pid, 0];
          }
        }),
      );

      if (cancelled) return;
      const map = {};
      counts.forEach(([pid, count]) => {
        map[pid] = count;
      });
      setReviewCounts((prev) => ({ ...prev, ...map }));
    };

    loadCounts();

    return () => {
      cancelled = true;
    };
  }, [paginatedProducts]);

  const activeFilterCount =
    filters.brand.length +
    // filters.ram.length +
    // filters.storage.length +
    filters.processor.length;

  const filterSections = [
    { title: "Brand", type: "brand", options: ["HP", "Dell", "Lenovo", "ASUS"] },
    // { title: "RAM", type: "ram", options: ["8GB", "16GB"] },
    // { title: "Storage", type: "storage", options: ["256GB", "512GB"] },
    { title: "Processor", type: "processor", options: ["Intel i3", "Intel i5", "Intel i7"] },
  ];

  /* ================= IMAGE HOVER ================= */
  const handleMouseEnter = (id, images) => {
    if (!images || images.length <= 1) return;

    setHoverIndex((prev) => ({ ...prev, [id]: 1 }));

    hoverTimers.current[id] = setInterval(() => {
      setHoverIndex((prev) => ({
        ...prev,
        [id]: ((prev[id] || 0) + 1) % images.length,
      }));
    }, 1500);
  };

  const handleMouseLeave = (id) => {
    clearInterval(hoverTimers.current[id]);
    setHoverIndex((prev) => ({ ...prev, [id]: 0 }));
  };

  if (loading) {
    return (
      <div className="text-center py-20">
        <p className="text-lg font-semibold">Loading products...</p>
      </div>
    );
  }

  return (
    <motion.div
      className="bg-[#f8f9fb] min-h-screen px-2 lg:py-10 py-4"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.8 }}
    >
      {/* TOP BAR */}
   <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 
                lg:mb-10
                grid grid-cols-1 md:grid-cols-3 
                items-center ">

  {/* LEFT - Breadcrumb */}
  <div className="flex flex-wrap items-center gap-2 sm:gap-3 
                  text-gray-500 text-xs sm:text-sm 
                  justify-center md:justify-start">

    <button
      onClick={() => navigate("/")}
      className="w-8 h-8 sm:w-9 sm:h-9 
                 flex items-center justify-center 
                 rounded-full border border-gray-300 
                 hover:bg-gray-100 transition"
    >
      <i className="fa-solid fa-chevron-left text-xs sm:text-sm"></i>
    </button>

    <button 
      onClick={() => navigate("/")} 
      className="hover:text-black transition"
    >
      Home
    </button>

    <span>/</span>

    <span className="text-black font-medium">
      All Products
    </span>
  </div>

  {/* CENTER - Title */}
  <div className="text-center">
    <h2 className="text-2xl sm:text-3xl md:text-4xl 
                   font-extrabold text-[#351F72] 
                   tracking-tight">
      All Products
    </h2>
  </div>

  

</div>


      {/* MAIN LAYOUT */}
      <div className="max-w-[1400px] mx-auto grid grid-cols-1 lg:grid-cols-[320px_1fr] gap-6">
        {/* MOBILE TOP FILTERS */}
        <div className="lg:hidden bg-white rounded-xl p-3 shadow-sm border border-[#e6ebfb]">
          <div className="flex items-center gap-2 overflow-x-auto pb-1">
            <button
              onClick={() => setMobileFiltersOpen((prev) => !prev)}
              className="shrink-0 inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[#1F3C88] text-white text-sm font-semibold"
            >
              <i className="fa-solid fa-sliders"></i>
              Filters
              {activeFilterCount > 0 && (
                <span className="inline-flex items-center justify-center min-w-5 h-5 rounded-full bg-white text-[#1F3C88] text-xs px-1">
                  {activeFilterCount}
                </span>
              )}
            </button>

            {filterSections.map((section) => (
              <button
                key={section.type}
                onClick={() => setMobileFiltersOpen(true)}
                className={`shrink-0 px-3 py-2 rounded-lg border text-xs font-semibold ${
                  filters[section.type].length > 0
                    ? "bg-[#1F3C88] text-white border-[#1F3C88]"
                    : "text-[#1F3C88] border-[#1F3C88]"
                }`}
              >
                {section.title}
              </button>
            ))}

            {activeFilterCount > 0 && (
              <button
                onClick={() =>
                  setFilters({ brand: [], ram: [], storage: [], processor: [] })
                }
                className="shrink-0 px-3 py-2 rounded-lg border border-[#E63946] text-[#E63946] text-xs font-semibold"
              >
                Clear
              </button>
            )}
          </div>

          {mobileFiltersOpen && (
            <div className="mt-3 pt-3 border-t">
              {/* SORT */}
              <details className="border rounded-lg mb-3 group" open>
                <summary className="px-4 py-3 cursor-pointer flex justify-between items-center text-sm font-medium hover:bg-gray-50 text-[#1F3C88]">
                  Price
                  <i className="fa-solid fa-chevron-down transition group-open:rotate-180"></i>
                </summary>

                <div className="px-4 pb-3 space-y-2 text-sm">
                  {[
                    { label: "Latest", value: "latest" },
                    { label: "Price: Low to High", value: "priceLow" },
                    { label: "Price: High to Low", value: "priceHigh" },
                  ].map((opt) => (
                    <label
                      key={opt.value}
                      className="flex items-center gap-2 cursor-pointer hover:text-black text-gray-700"
                    >
                      <input
                        type="radio"
                        name="sortByMobile"
                        className="accent-[#1F3C88]"
                        checked={sortBy === opt.value}
                        onChange={() => setSortBy(opt.value)}
                      />
                      {opt.label}
                    </label>
                  ))}
                </div>
              </details>

              {filterSections.map((section, idx) => (
                <details key={idx} className="border rounded-lg mb-3 group" open={idx === 0}>
                  <summary className="px-4 py-3 cursor-pointer flex justify-between items-center text-sm font-medium hover:bg-gray-50 text-[#1F3C88]">
                    {section.title}
                    <i className="fa-solid fa-chevron-down transition group-open:rotate-180"></i>
                  </summary>

                  <div className="px-4 pb-3 space-y-2 text-sm">
                    {section.options.map((opt) => (
                      <label
                        key={opt}
                        className="flex items-center gap-2 cursor-pointer hover:text-black text-gray-700"
                      >
                        <input
                          type="checkbox"
                          className="accent-[#1F3C88]"
                          checked={filters[section.type].includes(opt)}
                          onChange={() => toggleFilter(section.type, opt)}
                        />
                        {opt}
                      </label>
                    ))}
                  </div>
                </details>
              ))}

              <button
                onClick={() => setMobileFiltersOpen(false)}
                className="w-full mt-1 bg-[#1F3C88] text-white py-2 rounded-lg text-sm font-semibold"
              >
                Apply Filters
              </button>
            </div>
          )}
        </div>

        {/* FILTERS */}
        <div className="hidden lg:block bg-white rounded-xl p-6 shadow-lg h-fit w-full">
          <h3 className="font-semibold text-xl mb-4 text-[#1F3C88]">
            Filters
          </h3>

          {/* SORT */}
          <details className="border rounded-lg mb-4 group" open>
            <summary className="px-4 py-3 cursor-pointer flex justify-between items-center text-sm font-medium hover:bg-gray-50 text-[#1F3C88]">
              Price
              <i className="fa-solid fa-chevron-down transition group-open:rotate-180"></i>
            </summary>

            <div className="px-4 pb-3 space-y-2 text-sm">
              {[
                { label: "Latest", value: "latest" },
                { label: "Price: Low to High", value: "priceLow" },
                { label: "Price: High to Low", value: "priceHigh" },
              ].map((opt) => (
                <label
                  key={opt.value}
                  className="flex items-center gap-2 cursor-pointer hover:text-black text-gray-700"
                >
                  <input
                    type="radio"
                    name="sortByDesktop"
                    className="accent-[#1F3C88]"
                    checked={sortBy === opt.value}
                    onChange={() => setSortBy(opt.value)}
                  />
                  {opt.label}
                </label>
              ))}
            </div>
          </details>

          {filterSections.map((section, idx) => (
            <details key={idx} className="border rounded-lg mb-3 group">
              <summary className="px-4 py-3 cursor-pointer flex justify-between items-center text-sm font-medium hover:bg-gray-50 text-[#1F3C88]">
                {section.title}
                <i className="fa-solid fa-chevron-down transition group-open:rotate-180"></i>
              </summary>

              <div className="px-4 pb-3 space-y-2 text-sm">
                {section.options.map((opt) => (
                  <label
                    key={opt}
                    className="flex items-center gap-2 cursor-pointer hover:text-black text-gray-700"
                  >
                    <input
                      type="checkbox"
                      className="accent-[#1F3C88]"
                      checked={filters[section.type].includes(opt)}
                      onChange={() => toggleFilter(section.type, opt)}
                    />
                    {opt}
                  </label>
                ))}
              </div>
            </details>
          ))}
        </div>

        {/* PRODUCTS GRID */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className="grid gap-4 md:gap-6 grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4"
        >
          {paginatedProducts.map((product) => {
            const imgIndex = hoverIndex[product.id] || 0;

            return (
              <motion.div
  key={product.id}
  variants={cardVariants}
  whileHover={{
    y: -10,
    boxShadow: "0px 20px 40px rgba(0,0,0,0.12)",
  }}
  className="bg-white rounded-2xl shadow-md overflow-hidden flex flex-row sm:block"
>
  <div
    onMouseEnter={() =>
      handleMouseEnter(product.id, product.images)
    }
    onMouseLeave={() =>
      handleMouseLeave(product.id)
    }
    className="relative w-24 h-24 sm:w-full sm:h-44 flex-shrink-0 flex items-center justify-center bg-white p-2.5 sm:p-4"
  >
   
   <img
                src={logo}
                alt="PC Care Logo"
                className="absolute top-2 left-2 sm:top-3 sm:left-3 w-7 h-7 sm:w-10 sm:h-10 object-contain z-30 drop-shadow-md"
              />

    {/* PRODUCT IMAGE */}
    <img
      src={product.images?.[imgIndex]}
      alt={product.name}
      onClick={() =>
        navigate(`/products2/${product.id}`)
      }
      className="max-h-full max-w-full object-contain cursor-pointer transition-transform duration-500 sm:hover:scale-105"
    />
  </div>

  <div className="p-3 sm:p-4 flex-1 min-w-0 flex flex-col gap-1.5 sm:block">
    <h3
      title={product.name || ""}
      className="font-semibold text-sm sm:text-[15px] leading-snug text-[#1F3C88] truncate"
    >
      {product.name}
    </h3>

    <div className="flex items-center gap-2 text-xs sm:text-sm sm:mt-2">
      <span className="text-yellow-500">{"\u2605"} {product.rating}</span>
      <span className="text-gray-500">({reviewCounts[product.id] ?? product.reviews ?? 0})</span>
    </div>

    <div className="flex items-center flex-wrap gap-2 mt-1 sm:mt-3">
      <span className="text-base sm:text-lg font-bold text-[#E63946] leading-none">
        {"\u20B9"}{product.newPrice.toLocaleString()}
      </span>
      {product.oldPrice > product.newPrice && product.oldPrice > 0 && (
        <span className="text-xs text-gray-400 line-through leading-none">
          {"\u20B9"}{product.oldPrice.toLocaleString()}
        </span>
      )}
      {product.off > 0 && (
        <span className="text-green-600 text-xs font-semibold bg-green-100 px-2 py-1 rounded-full">
          {product.off}% OFF
        </span>
      )}
    </div>

    <div className="mt-1.5 sm:mt-4 flex gap-2">
      <button
        onClick={() => {
          handleAddToCart(product);
          navigate(`/products2/${product.id}?type=product`);
        }}
        className="flex-1 sm:flex-none sm:w-1/2 whitespace-nowrap
                  bg-gradient-to-r from-[#1F3C88] to-[#3f5bdc] 
                  hover:from-[#162e6f] hover:to-[#2f47c4] 
                  text-white 
                  py-2.5 sm:py-2 
                  rounded-xl 
                  text-xs sm:text-sm 
                  font-semibold 
                  transition 
                  flex items-center 
                  justify-center 
                  gap-2"
      >
        <span className="sm:hidden">Book</span>
        <span className="hidden sm:inline">Buy Now</span>
      </button>

      <a
        href={getProductEnquiryLink(
          { ...product, selling_price: product.newPrice },
          { type: "product" }
        )}
        target="_blank"
        rel="noopener noreferrer"
        className="flex-1 sm:flex-none sm:w-1/2 whitespace-nowrap
                  border-2 border-[#1F3C88] 
                  text-[#1F3C88] 
                  hover:bg-[#1F3C88] 
                  hover:text-white 
                  py-2.5 sm:py-2 
                  rounded-xl 
                  text-xs sm:text-sm 
                  font-semibold 
                  transition 
                  flex items-center 
                  justify-center 
                  gap-2"
      >
        <span className="sm:hidden">Enquiry</span>
        <span className="hidden sm:inline">Enquiry Now</span>
      </a>
    </div>


  </div>
</motion.div>

            );
          })}
        </motion.div>
      </div>

      {paginatedProducts.length === 0 && (
        <div className="max-w-[1400px] mx-auto mt-6 rounded-xl bg-white p-6 text-center text-sm text-gray-600 shadow-sm">
          {searchQuery
            ? `No products found for "${searchQuery}". Try: best laptop, new laptop, i5, 16GB, Dell.`
            : "No products found. Try searching with: best laptop, new laptop, i5, 16GB, Dell."}
        </div>
      )}

    
{totalPages > 0 && (
  <div className="flex justify-center items-center gap-2 mt-10 flex-wrap">
    <button
      onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
      disabled={currentPage === 1}
      className={`px-4 py-2 rounded-lg border ${
        currentPage === 1
          ? "text-gray-400 border-gray-300"
          : "text-[#1F3C88] border-[#1F3C88] hover:bg-[#1F3C88] hover:text-white"
      }`}
    >
      Prev
    </button>

    {Array.from({ length: totalPages }, (_, i) => i + 1).map((num) => (
      <button
        key={num}
        onClick={() => setCurrentPage(num)}
        className={`px-4 py-2 rounded-lg border ${
          currentPage === num
            ? "bg-[#1F3C88] text-white border-[#1F3C88]"
            : "text-[#1F3C88] border-[#1F3C88] hover:bg-[#1F3C88] hover:text-white"
        }`}
      >
        {num}
      </button>
    ))}

    <button
      onClick={() =>
        setCurrentPage((p) => Math.min(p + 1, totalPages))
      }
      disabled={currentPage === totalPages}
      className={`px-4 py-2 rounded-lg border ${
        currentPage === totalPages
          ? "text-gray-400 border-gray-300"
          : "text-[#1F3C88] border-[#1F3C88] hover:bg-[#1F3C88] hover:text-white"
      }`}
    >
      Next
    </button>
  </div>
)}
    </motion.div>
  );
};

export default Products;
